/**
 * ExperimentSignificance — T-164-003 admin section (batch 4 S9).
 *
 * Embedded in TexasAnalytics: paste an experiment metrics sheet as JSON
 * ([{ metric, control:{success,total}, variants:[{variant,success,total}] }]
 * — the shape the LP-side variant pipeline produces) and the table flags the
 * p<0.05 winner per metric per variant via twoProportionZTest. Pure/local —
 * no API dependency, so the admin surface works even while DEPLOY is blocked.
 */
import React, { useState } from 'react';
import {
  CompareSheet,
  MetricComparison,
  compareMetrics,
  formatRate,
  formatPValue,
  winnerLabel,
} from '../../../services/experimentStats';

const EXAMPLE_SHEET: CompareSheet[] = [
  {
    metric: 'completion_rate',
    control: { success: 120, total: 1000 },
    variants: [
      { variant: 'control', success: 120, total: 1000 },
      { variant: 'treatment', success: 170, total: 1014 },
    ],
  },
  {
    metric: 'booking_rate',
    control: { success: 40, total: 1000 },
    variants: [
      { variant: 'control', success: 40, total: 1000 },
      { variant: 'treatment', success: 63, total: 1014 },
    ],
  },
];

export const ExperimentSignificance: React.FC = () => {
  const [sheetJson, setSheetJson] = useState<string>(JSON.stringify(EXAMPLE_SHEET, null, 2));
  const [sheets, setSheets] = useState<CompareSheet[]>(EXAMPLE_SHEET);
  const [parseError, setParseError] = useState<string | null>(null);

  const applySheet = () => {
    try {
      const parsed = JSON.parse(sheetJson) as CompareSheet[];
      if (!Array.isArray(parsed) || parsed.length === 0) {
        throw new Error('expected a non-empty array of metric sheets');
      }
      setSheets(parsed);
      setParseError(null);
    } catch (err: any) {
      setParseError(err?.message || 'Invalid JSON');
    }
  };

  const metrics: MetricComparison[] = sheets.map((s) => compareMetrics(s));

  return (
    <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 mt-4" data-testid="experiment-significance">
      <h2 className="text-lg font-bold text-gray-900 mb-1">Experiment Significance</h2>
      <p className="text-sm text-gray-600 mb-3">
        Two-proportion z-test per metric — a variant is flagged the winner when its p-value is
        below 0.05. Paste the metrics sheet (JSON) from the variant pipeline.
      </p>

      <textarea
        data-testid="sheet-input"
        value={sheetJson}
        onChange={(e) => setSheetJson(e.target.value)}
        rows={8}
        spellCheck={false}
        className="w-full border border-gray-300 rounded-md p-2 text-xs font-mono mb-2"
      />
      {parseError && (
        <p className="text-xs text-red-600 mb-2" data-testid="sheet-error">{parseError}</p>
      )}
      <button
        type="button"
        data-testid="sheet-apply"
        onClick={applySheet}
        className="bg-blue-600 text-white text-sm font-semibold px-4 py-2 rounded hover:bg-blue-700 mb-4"
      >
        Compute p-values
      </button>

      {metrics.map((metric) => (
        <div key={metric.metric} className="mb-4" data-testid={`metric-${metric.metric}`}>
          <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider mb-2">
            {metric.metric}
          </h3>
          <table className="w-full text-sm text-left" data-testid="sig-table">
            <thead>
              <tr className="border-b border-gray-200 text-xs text-slate-500 uppercase tracking-wider">
                <th className="py-2 pr-4">Variant</th>
                <th className="py-2 pr-4">Rate</th>
                <th className="py-2 pr-4">Δ vs control</th>
                <th className="py-2 pr-4">p-value</th>
                <th className="py-2">Winner (p&lt;0.05)</th>
              </tr>
            </thead>
            <tbody>
              {metric.results.map((r) => (
                <tr key={r.variant} className="border-b border-gray-100" data-testid={`sig-row-${r.variant}`}>
                  <td className="py-2 pr-4 font-medium">{r.variant}</td>
                  <td className="py-2 pr-4" data-testid={`sig-rate-${metric.metric}-${r.variant}`}>
                    {formatRate(r.rate)}
                  </td>
                  <td className="py-2 pr-4" data-testid={`sig-delta-${metric.metric}-${r.variant}`}>
                    {Number.isFinite(r.delta) ? `${(r.delta * 100).toFixed(2)}%` : '—'}
                  </td>
                  <td className="py-2 pr-4" data-testid={`sig-pvalue-${metric.metric}-${r.variant}`}>
                    {formatPValue(r.pValue)}
                  </td>
                  <td className="py-2" data-testid={`sig-winner-${metric.metric}-${r.variant}`}>
                    {winnerLabel(r)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </section>
  );
};

export default ExperimentSignificance;